import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  useColorModeValue,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import Loader from 'components/Loader/Loader';

export interface SearchBarProps {
  variant?: string;
  background?: string;
  children?: any;
  placeholder?: string;
  borderRadius?: string | number;
  onChange?: (input: string) => void;
  showResultBox?: boolean;
  resultsLoading?: boolean;
  hasValidResults?: boolean;
  results?: JSX.Element[] | JSX.Element;
  [x: string]: any;
}

export enum SearchBarConfig {
  ON_CHANGE_DELAY = 500,
  RESULTS_HIDE_DELAY_ON_BLUR = 200
}

export function SearchBar(props: SearchBarProps) {
  const {
    variant,
    background,
    children,
    placeholder,
    borderRadius,
    onChange,
    showResultBox = false,
    resultsLoading = false,
    hasValidResults = true,
    results = [],
    ...rest
  } = props;

  const [value, setValue] = useState<string>('');
  const [showResults, setShowResults] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Chakra Color Mode
  const searchIconColor = useColorModeValue('gray.700', 'white');
  const inputBg = useColorModeValue('secondaryGray.300', 'navy.900');
  const inputText = useColorModeValue('gray.700', 'gray.100');
  const resultsBackgroundColor = useColorModeValue('white', 'navy.800');

  useEffect(() => {
    const timeOutId = setTimeout(() => onChange && onChange(value), SearchBarConfig.ON_CHANGE_DELAY);
    return () => clearTimeout(timeOutId);
  }, [value]);

  // Handle click outside to close results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        resultsRef.current &&
        !resultsRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    };

    if (showResults) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showResults]);

  const handleChange = (event: React.FormEvent<HTMLInputElement>) => {
    const inputValue: string = (event.target as HTMLTextAreaElement).value;
    setValue(inputValue);
  };

  function renderResults() {
    if (showResults && showResultBox && (resultsLoading || hasValidResults)) {
      const content = resultsLoading ? <Loader /> : results;
      return (
        <Flex
          ref={resultsRef}
          direction="column"
          overflow="scroll"
          minHeight="100px"
          maxHeight="600px"
          minWidth="400px"
          maxWidth="800px"
          background={resultsBackgroundColor}
          position="absolute"
          marginTop="50px"
          borderWidth='1px'
          borderRadius='lg'
          zIndex={9999}
          boxShadow="xl"
          onMouseDown={(e) => {
            // Prevent input from losing focus when clicking inside results
            e.preventDefault();
          }}
        >
          {content}
        </Flex>
      );
    }
  }

  return (
    <Box ref={containerRef} position="relative" w={{ base: '100%', md: '200px' }}>
      <InputGroup {...rest}>
        <InputLeftElement
          children={
            <IconButton
              aria-label="search"
              bg="inherit"
              borderRadius="inherit"
              _active={{
                bg: 'inherit',
                transform: 'none',
                borderColor: 'transparent',
              }}
              _hover={{
                background: 'none',
              }}
              _focus={{
                background: 'none',
                boxShadow: 'none',
              }}
              icon={<SearchIcon color={searchIconColor} w="15px" h="15px" />}
            />
          }
        />
        <Input
          variant="search"
          fontSize="sm"
          bg={background ? background : inputBg}
          color={inputText}
          fontWeight="500"
          _placeholder={{ color: 'gray.400', fontSize: '14px' }}
          borderRadius={borderRadius ? borderRadius : '30px'}
          placeholder={placeholder ? placeholder : 'Search...'}
          value={value}
          onChange={handleChange}
          onFocus={() => setShowResults(true)}
        />
      </InputGroup>
      {renderResults()}
    </Box>
  );
}
